import { useRolodexEdit } from 'features/fde'
import { useKestrel, useKestrelViewerContext } from 'features/kestrelViewer'
import { MarkupLabelInput } from 'features/kestrelViewer/ui/popupMenu/shared/MarkupLabelInput'
import { MarkupNotesInput } from 'features/kestrelViewer/ui/popupMenu/shared/MarkupNotesInput'
import {
  InputGroup,
  InputHeader,
} from 'features/kestrelViewer/ui/popupMenu/shared/MarkupPopupContainer'
import { useEscapeKey } from 'features/kestrelViewer/ui/popupMenu/shared/useEscapeKey'
import {
  formatArea,
  formatPerimeter,
} from 'features/kestrelViewer/utils/unitConversionUtils'
import { useGeckoLocale } from 'features/locale'
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { KVInspectionArea, RolodexEntryPatchJson } from 'types'

import { FormsDropdown } from './FormsDropdown'
import { InspectionAreaStatusDropdown } from './InspectionAreaStatusDropdown'

const AreaPerimeterSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 8px;
  padding: 8px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
`

const AreaPerimeterValue = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
`

const AreaPerimeterLabel = styled.div`
  font-size: 0.6rem;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.05rem;
`

export const InspectionAreaMenu: React.FC<{
  selectedInspectionArea: KVInspectionArea
}> = ({ selectedInspectionArea }) => {
  const { currentUnit, setSelectedMarkup } = useKestrelViewerContext()
  const { rolodexPatch } = useRolodexEdit()
  const {
    kestrelModel,
    inspectionAreas,
    setInspectionAreas,
    updateInspectionArea,
  } = useKestrel()
  const { locale } = useGeckoLocale()
  const labelInputRef = useRef<HTMLInputElement>(null)
  const notesInputRef = useRef<HTMLTextAreaElement>(null)
  const [localLabel, setLocalLabel] = useState(selectedInspectionArea.label)

  // Handle escape key to close popup
  useEscapeKey(() => setSelectedMarkup(undefined))

  // Focus on label input when popup opens
  useEffect(() => {
    if (labelInputRef.current) {
      labelInputRef.current.focus()
    }
  }, [])

  const handleTabKey = (
    e: React.KeyboardEvent,
    targetRef: React.RefObject<HTMLElement | null>
  ) => {
    if (e.key === 'Tab') {
      e.preventDefault()
      if (targetRef.current) {
        targetRef.current.focus()
      }
    }
  }

  const handleLabelEnter = () => {
    if (!localLabel || localLabel.trim().length === 0) return
    updateInspectionArea(
      selectedInspectionArea,
      'inspectionAreaLabel',
      (ia) => {
        ia.label = localLabel
        return ia
      },
      localLabel
    )
  }

  const handleLabelEscape = () => {
    setLocalLabel(selectedInspectionArea.label)
  }

  const setNotes = async (s: string) => {
    if (!inspectionAreas || !kestrelModel?.id) return

    // Update local state
    const updatedInspectionAreas = inspectionAreas.map((ia) =>
      ia.id === selectedInspectionArea.id ? { ...ia, notes: s } : ia
    )
    setInspectionAreas(updatedInspectionAreas)

    // Update rolodex
    const patchData: RolodexEntryPatchJson = {
      id: kestrelModel.id,
      data: {
        inspectionAreas: updatedInspectionAreas.map((ia) =>
          KVInspectionArea.toJSON(ia)
        ),
      },
    }

    try {
      await rolodexPatch([patchData])
    } catch (error) {
      console.error('Failed to update inspection area:', error)
    }
  }

  return (
    <>
      <InputGroup>
        <InputHeader>Label</InputHeader>
        <MarkupLabelInput
          ref={labelInputRef}
          value={localLabel}
          onChange={setLocalLabel}
          placeholder='New Inspection Area'
          onEnter={handleLabelEnter}
          onEscape={handleLabelEscape}
          onTabKey={(e) => handleTabKey(e, notesInputRef)}
          saveOnBlur={true}
          requireNonEmpty={true}
          warningText='Label is required.'
        />
      </InputGroup>
      {selectedInspectionArea.area !== undefined &&
        selectedInspectionArea.perimeter !== undefined && (
          <AreaPerimeterSection>
            <div>
              <AreaPerimeterLabel>Area</AreaPerimeterLabel>
              <AreaPerimeterValue>
                {formatArea(selectedInspectionArea.area, currentUnit, locale)}
              </AreaPerimeterValue>
            </div>
            <div>
              <AreaPerimeterLabel>Perimeter</AreaPerimeterLabel>
              <AreaPerimeterValue>
                {formatPerimeter(
                  selectedInspectionArea.perimeter,
                  currentUnit,
                  locale
                )}
              </AreaPerimeterValue>
            </div>
          </AreaPerimeterSection>
        )}
      <InputGroup>
        <InputHeader>Status</InputHeader>
        <InspectionAreaStatusDropdown
          selectedInspectionArea={selectedInspectionArea}
        />
      </InputGroup>
      <FormsDropdown inspectionAreaId={selectedInspectionArea.id} />
      <InputGroup>
        <InputHeader>Notes</InputHeader>
        <MarkupNotesInput
          ref={notesInputRef}
          value={selectedInspectionArea.notes}
          onChange={setNotes}
          placeholder='Add notes...'
          onTabKey={(e) => handleTabKey(e, labelInputRef)}
        />
      </InputGroup>
    </>
  )
}
